import { useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useLiveQuery } from 'dexie-react-hooks';
import { db } from '../../db/local.js';
import { RecipeDetail } from './RecipeDetail.js';
import { RecipeEditor } from './RecipeEditor.js';
import { RecipeForm } from './RecipeForm.js';
import { RecipeGrid } from './RecipeGrid.js';
import { RecipeImport } from './RecipeImport.js';

type RecipeView =
  | { mode: 'list' }
  | { mode: 'detail'; id: string }
  | { mode: 'edit'; id: string }
  | { mode: 'create' }
  | { mode: 'import' };

/**
 * Gerichte: Kacheln → Gericht-Seite → Bearbeiten. Ein Zustand statt dreier
 * Schalter, damit nie zwei Ansichten zugleich offen sind.
 */
export function RecipeLibrary(): React.JSX.Element {
  const { t } = useTranslation();
  const [view, setView] = useState<RecipeView>({ mode: 'list' });
  const editing = useLiveQuery(
    () => (view.mode === 'edit' ? db.recipes.get(view.id) : undefined),
    [view.mode === 'edit' ? view.id : null],
  );

  const top = useRef<HTMLDivElement>(null);
  const shownView = useRef(view);
  useEffect(() => {
    // Beim Aufbauen nicht: Die Bibliothek entsteht auch beim Wischen
    // (24.10.2026) — dann sprang die Seite zur Liste hinunter. Verglichen wird
    // mit der zuletzt gezeigten Ansicht, damit ein doppelter Effekt-Lauf
    // (StrictMode) nicht doch springt.
    if (shownView.current === view) return;
    shownView.current = view;
    // Beim Wechsel der Ansicht nach oben — sonst öffnet die Gericht-Seite dort,
    // wo man in der Liste gerade war. `scrollIntoView` fehlt in jsdom.
    top.current?.scrollIntoView?.({ block: 'start' });
  }, [view]);

  const anchor = <div ref={top} className="scroll-mt-20" aria-hidden="true" />;

  if (view.mode === 'detail') {
    // `min-h-dvh`: Die Seite lädt ihre Daten erst — ohne Mindesthöhe wäre sie
    // beim Scrollen noch leer, und der Browser bliebe zu weit oben stehen.
    return (
      <div className="flex min-h-dvh flex-col">
        {anchor}
        <RecipeDetail
          recipeId={view.id}
          onBack={() => setView({ mode: 'list' })}
          onEdit={() => setView({ mode: 'edit', id: view.id })}
          onOpen={(id) => setView({ mode: 'edit', id })}
        />
      </div>
    );
  }

  if (view.mode === 'import') {
    return (
      <div className="flex min-h-dvh flex-col gap-3">
        {anchor}
        <RecipeImport
          onDone={(id) => setView({ mode: 'edit', id })}
          onCancel={() => setView({ mode: 'list' })}
        />
      </div>
    );
  }

  if (view.mode === 'edit' || view.mode === 'create') {
    const back = () =>
      setView(view.mode === 'edit' ? { mode: 'detail', id: view.id } : { mode: 'list' });
    return (
      <div className="flex min-h-dvh flex-col gap-3">
        {anchor}
        <button
          type="button"
          onClick={back}
          className="kw-btn kw-btn-quiet kw-btn-small self-start px-3"
        >
          ← {t('dish.back')}
        </button>
        {view.mode === 'create' ? (
          <RecipeForm
            onDone={(id) => setView({ mode: 'edit', id })}
            onCancel={() => setView({ mode: 'list' })}
          />
        ) : editing ? (
          <RecipeEditor recipe={editing} onDone={back} />
        ) : null}
      </div>
    );
  }

  return (
    <>
      {anchor}
      <RecipeGrid
        onOpen={(id) => setView({ mode: 'detail', id })}
        onCreate={() => setView({ mode: 'create' })}
        onImport={() => setView({ mode: 'import' })}
      />
    </>
  );
}
